<script setup>
import { computed, onMounted, ref, watch } from "vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonBadge from "@/components/ArgonBadge.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import VehicleAvatar from "@/components/VehicleAvatar.vue";
import moment from "moment";
import { useRouter } from "vue-router";
import { defaultFilterDates } from "@/maestros/defaultDatesValues";
import { argonTableStore } from "@/_store/argonTableStore";
import { routeStore } from "@/_store/routeStore";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";
import {
  formatAlertsTooltipHtml,  
  formatVolume,
  formatSecondsAsMinutes,
  formatTimeWindow,
  formatWeight,
  normalizeAlerts,
} from "@/utils/formatters";
import RouteDetailModal from "@/views/routes/components/RouteDetailModal.vue";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";

const tableStore = argonTableStore();
const routesStore = routeStore();
const userStores = userStore();
const router = useRouter();
const snackbar = ref(null);
const selectedRouteId = ref("");
const showRouteDetailModal = ref(false);
const customFilterDate = ref(routesStore.customFilterDate);
const selectedRouteRow = computed(() => {
  const selectedIds = getSelectedRouteIds();
  if (selectedIds.length === 0) return null;
  const rows = Array.isArray(tableItems.value?.data) ? tableItems.value.data : [];
  return rows.find((item) => item?._id === selectedIds[0]) || null;
});
const selectedPlanId = computed(() => selectedRouteRow.value?.plan_id || "");
const canOpenPlan = computed(
  () => Boolean(selectedPlanId.value) && getSelectedRouteIds().length <= 1
);
const openPlanTooltip = computed(() => {
  if (getSelectedRouteIds().length > 1) {
    return "Debe seleccionar una única ruta para ir al plan.";
  }
  return "";
});

const fieldFilters = ref([
  {
    field: "name",
    description: "Vehículo",
  },
  {
    field: "model",
    description: "Modelo",
  },
]);
const statusOptions = Object.entries(ESTATUS_ENUM.route).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);
const defaultFilters = ref([
  {
    field: "status",
    label: "Estado",
    multi: true,
    options: statusOptions,
  },
  {
    field: "has_alerts",
    label: "Alertas",
    multi: false,
    options: [
      { label: "Con alertas", value: true },
      { label: "Sin alertas", value: false },
    ],
  },
  {
    field: "created_at",
    label: "Fecha",
    multi: false,
    isDate: true,
    options: [
      ...defaultFilterDates,
      {
        label: "Personalizado",
        value: customFilterDate,
        isCustomDate: true,
        storage: routesStore,
        formatDate: "DD/MM/YYYY",
      },
    ],
  },
]);

const routeRows = computed(() => {
  const source = Array.isArray(routesStore.routes?.data) ? routesStore.routes.data : [];
  const data = source.map((routeItem) => {
    const warningItems = normalizeAlerts(routeItem?.alerts);
    return {
      ...routeItem,
      hasWarning: warningItems.length > 0,
      warningTooltip: formatAlertsTooltipHtml(warningItems),
    };
  });

  const total = routesStore.routes?.total;

  return {
    total,
    data,
  };
});
const tableItems = computed(() => routeRows.value);

const columns = ref([
  { header: "Fecha", field: "created_at", sort: false, position: "start" },
  { header: "Vehículo", field: "vehicle", sort: false,  position: "start" },
  { header: "Paradas", field: "stops_counter", sort: false, position: "center" },
  
  { header: "Kilómetros", field: "km", sort: false, position: "start" },
  { header: "Peso", field: "weight", sort: false, position: "start" },
  { header: "Horario", field: "time_window", sort: false, position: "start" },
  { header: "Estado", field: "status", sort: false, position: "center", width: "w-1" },
  { header: "", field: "actions", sort: false, position: "center", width: "w-1" },
]);

function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

function toCount(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
}

function reloadPage() {
  userStores.filtersPage.routes = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };
  routesStore.getRoutes(tableStore.pagination, tableStore.query, tableStore.sort);
}

async function exportRoutes() {
  const bytes = await routesStore.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function closeSnackbar() {
  snackbar.value = null;
}

function closeRouteDetailModal() {
  showRouteDetailModal.value = false;
  selectedRouteId.value = "";
}

function getSelectedRouteIds() {
  return Array.isArray(tableStore.checkedList)
    ? tableStore.checkedList.map((id) => id ?? "").filter(Boolean)
    : [];
}

function showSelectionError(message) {
  snackbar.value = {
    title: "Error",
    message,
    type: "danger",
    color: "white",
  };
}

function openPlan() {
  const selectedIds = getSelectedRouteIds();
  if (selectedIds.length > 1) {
    showSelectionError("Solo puede seleccionar una ruta.");
    return;
  }
  if (!selectedPlanId.value) {
    showSelectionError("La ruta seleccionada no tiene plan asociado.");
    return;
  }
  router.push(`/plan/${selectedPlanId.value}`);
}

function getRouteAccentColor(item) {
  return item?.route_color_hex || "#5baee9";
}

function openRouteDetail(item) {
  if (!item) return;
  selectedRouteId.value = item._id;
  showRouteDetailModal.value = true;
  routesStore.getRouteById(item._id).catch(() => {});
}

function getRowActionsMenuId(routeItem) {
  return `routes-table-row-actions-${String(routeItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions(routeItem) {
  const hasPlan = Boolean(routeItem?.plan_id);

  return [
    [{ label: "Detalle", event: "detail" }],
    [
      {
        label: "Ir a plan",
        event: "openPlan",
        disabled: !hasPlan,
        tooltip: hasPlan ? "" : "La ruta no tiene plan asociado.",
      },
    ],
  ];
}

function onRowAction(routeItem, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !routeItem) return;

  if (actionEvent === "detail") {
    openRouteDetail(routeItem);
    return;
  }

  if (actionEvent === "openPlan") {
    if (!routeItem?.plan_id) {
      showSelectionError("La ruta seleccionada no tiene plan asociado.");
      return;
    }
    router.push(`/plan/${routeItem.plan_id}`);
  }
}

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.routes?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});

watch(
  () => routesStore.error,
  (value) => {
    if (!value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card">
    <div class="card-body">
      <argon-table
        v-if="routesStore.routes"
        :columns="columns"
        :items="tableItems"
        :field-filters="fieldFilters"
        :default-filters="defaultFilters"
        :show-new-button="false"
        :show-config-option="true"
        :show-importar="false"
        @filter-changed="reloadPage"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @export-data="exportRoutes"
        @on-click="openRouteDetail"
      >
        <template #buttons>
          <div class="dropdown d-inline" data-dropdown-static style="--dropdown-gap: 4px">
            <argon-button
              id="navbarDropdownPrinter"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
              ><i class="fa fa-exchange me-sm-2"></i>Acciones</argon-button
            >
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="navbarDropdownPrinter"
              style
            >
              <li
                v-tooltip="openPlanTooltip"
                :class="{ disabled: !canOpenPlan }"
              >
                <a
                  class="dropdown-item border-radius-md"
                  :aria-disabled="!canOpenPlan"
                  @click.prevent="openPlan"
                  >Ir a plan</a
                >
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
            <argon-td
            :position="getPosition('created_at')"
            :text-primary="moment(item.created_at).format('DD/MM/YYYY')"
          />
          <argon-td
            :position="getPosition('vehicle')"
            :text-primary="item.name"
            :text-secondary="item.model"
            max-width="180px"
          >
            <template #prepend>
              <vehicle-avatar
                :label="item.name"
                :abbreviation="item.abbreviation"
                :accent-color="getRouteAccentColor(item)"
                :size="28"
                :has-alert="item.hasWarning"
                :alert-tooltip="item.warningTooltip"
                alert-label="Alertas de ruta"
              />
            </template>
          </argon-td>
          <argon-td :position="getPosition('stops_counter')">
            <argon-badge size="sm" color="secondary">{{ toCount(item.stops_counter) }}</argon-badge>
          </argon-td>
          <argon-td
            :text-primary="`${item.km} km`"
            :text-secondary="formatSecondsAsMinutes(item.total_time)"
            :position="getPosition('km')"
          />
          <argon-td
            :text-primary="formatWeight(item.max_weight)"
            :text-secondary="formatVolume(item.max_volume)"
            :position="getPosition('weight')"
          />
          <argon-td
            :text-primary="formatTimeWindow(item.time_window)"
            :position="getPosition('time_window')"
          />
          <argon-td :position="getPosition('status')">
            <argon-status entity="route" :status="item.status" width="100px" />
          </argon-td>
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions(item)"
                aria-label="Acciones de ruta"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>
  </div>

  <route-detail-modal
    v-if="showRouteDetailModal && selectedRouteId"
    modal-id="routes-table-detail-modal"
    :route-id="selectedRouteId"
    @close="closeRouteDetailModal"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
